// import React, { useState, useEffect } from 'react';
// import { Pie } from '@ant-design/charts';

// const PieChartCard=({
//   activitiesData
// }) => {
//   var data = [
//     {
//       type: 'Scheduled',
//       value: activitiesData?.scheduled,
//     },
//     {
//       type: 'On-going',
//        value: activitiesData?.ongoing,
//     },
//     {
//       type: 'Completed',
//       value: activitiesData?.completed,
//     },
//     {
//       type: 'Cancelled',
//       value: activitiesData?.cancelled,
//     },
//   ];

//   var config = {
//     appendPadding: 8,
//     data: data,
//      angleField: 'value',
//     colorField: 'type',
//     color: ({ type }) => {
//       if(type === 'Scheduled'){
//         return '#9463F9';
//       }
//       else if(type === 'On-going'){
//         return '#FF414D';
//       }
//       else if(type === 'Completed'){
//         return '#579EFF'
//       }
//       else if(type === 'Cancelled'){
//         return '#AEAEAE'
//       }
//     },
//     radius: 1,
//     innerRadius: 0.6,
//     label: {
//       type: 'inner',
//       offset: '-50%',
//       content: '',
//       autoFit: true,
//       legend: false,
//       style: {
//         textAlign: 'center',
//         fontSize: 14,
//       },
//     },
//     // interactions: [{ type: 'element-selected' }, { type: 'element-active' }],
//     statistic: {
//       title: false,
//       content: {
//         style: {
//           whiteSpace: 'pre-wrap',
//           overflow: 'hidden',
//           textOverflow: 'ellipsis',
//           fontSize:10
//         },
//         content: `Total live classes ${activitiesData?.scheduled+activitiesData?.ongoing+activitiesData?.completed+activitiesData?.cancelled}`,
//       },
//     },
//   };

//   return <Pie {...config} />;
// };

// export default PieChartCard;

import React from 'react';
import { Pie } from '@ant-design/charts';
import useChartBox from './useChartBox';


function arePropsEqual(oldProps, newProps) {
  return JSON.stringify(oldProps) === JSON.stringify(newProps);
}
const PieChartCard = React.memo(({ activitiesData }) => {
  // g2plot picks the canvas size once, when the chart is constructed, and falls
  // back to a hardcoded 400x400 if the box it is handed measures 0 on that one
  // tick. A 400px-wide donut inside a ~280px card is the sideways overflow this
  // page showed at times (and that a refresh 'fixed', because reloading usually
  // wins the timing race). This measures the box ourselves and only mounts the
  // chart once there is a real size to mount it into - see useChartBox.js.
  const { boxRef, onReady, ready } = useChartBox();

  // Single source of truth for the donut's height: it sizes both the config and
  // the box below, so the two can't drift apart.
  const chartHeight = window.innerWidth <= 1024 ? 230 : 250;

  // Scheduled was already part of the total in the middle of the ring but had no slice
  // and no legend entry of its own, so a day's scheduled classes were counted and then
  // never shown. Its blue is the same #5B73D6 the Summary chart uses for Scheduled, so
  // the two charts on this row agree on what the colour means.
  const data = [
    {
      type: 'Scheduled',
      value: activitiesData?.scheduled || 0,
    },
    {
      type: 'On-going',
      value: activitiesData?.ongoing || 0,
    },
    {
      type: 'Completed',
      value: activitiesData?.completed || 0,
    },
    {
      type: 'Cancelled',
      value: activitiesData?.cancelled || 0,
    },
  ];

  const totalClasses = (activitiesData?.scheduled || 0) + (activitiesData?.ongoing || 0) + (activitiesData?.completed || 0) + (activitiesData?.cancelled || 0);

  const config = {
    appendPadding: 8,
    // Fixed height instead of auto-fitting the wrapping div: the built-in
    // legend used to reserve its own space inside the chart's auto-fit area,
    // but now that the legend renders as separate HTML below the chart (see
    // LEGEND_ITEMS/JSX), the chart needs to give up that space explicitly or
    // it renders at full container height and the ring overlaps the legend.
    // Slightly shorter on iPad-ish widths (<=1024, same breakpoint the
    // sidebar auto-collapse uses) to leave a bit more room for the legend.
    height: chartHeight,
    data,
    angleField: 'value',
    colorField: 'type',

    color: ({ type }) => {
      if (type === 'Scheduled') return '#5B73D6';
      if (type === 'On-going') return '#FFC107';
      if (type === 'Completed') return '#00A65A';
      if (type === 'Cancelled') return '#FF4D4F';
      return '#ccc';
    },

    /** ✅ SHRINK PIE SIZE */
    radius: 0.85,        // ⬅️ outer circle smaller
    innerRadius: 0.80,   // ⬅️ donut thickness balanced

    label: false,

    // actually be rounded; G2's canvas-drawn "square"/custom rect symbol
    // wasn't rendering reliably in this chart library version.
    legend: false,
    tooltip: {
      showTitle: false,
      showMarkers: false,
      formatter: (datum) => {
        return { name: datum.type, value: datum.value };
      }
    },

    statistic: {
      title: false,
      content: {
        content: `${totalClasses} ${totalClasses === 1 ? 'class' : 'classes'}\nscheduled`,
        style: {
          width: window.innerWidth <= 480 ? '80px' : '100px',
          fontSize: window.innerWidth <= 480 ? 12 : 14,
          fontWeight: 600,
          lineHeight: '20px',
          textAlign: 'center',
          whiteSpace: 'pre-line',
        },
      },
    },

    interactions: [{ type: 'element-active' }],
  };

  const LEGEND_ITEMS = [
    { label: 'Scheduled', color: '#5B73D6' },
    { label: 'On-going', color: '#FFC107' },
    { label: 'Completed', color: '#00A65A' },
    { label: 'Cancelled', color: '#FF4D4F' },
  ];

  return (
    // Column flex at the card's full height with `marginTop: auto` on the legend:
    // that drops the legend to the bottom of the card and gives the leftover space
    // to the gap above it, instead of the legend sitting tight under the ring. The
    // donut itself is untouched - its size comes from config.height/radius above,
    // and the space being handed to the gap is space the chart was already not using.
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Explicit height (not flex) so the box has a size of its own even before
          the chart is in it - a box sized by its chart could never open the
          `ready` gate. flexShrink: 0 keeps the donut at its configured height so
          the leftover space still goes to the gap above the legend, exactly as
          the marginTop: auto below intends. */}
      <div ref={boxRef} style={{ height: chartHeight, flexShrink: 0 }}>
        {ready ? <Pie {...config} onReady={onReady} style={{ width: '100%', height: '100%' }} /> : null}
      </div>
      {/* Two columns, so the four items always split two above two below. A wrapping
          flex row can't guarantee that - whether a third item fits is down to the card's
          width, and at this card's width it does. columnGap is what sets the space down
          the middle; rowGap stays tight so the two lines don't drift apart. Restore this
          for a single wrapping row: display: 'flex', flexWrap: 'wrap', gap: 12 */}
      <div
        className="schedule-donut-legend"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, max-content)',
          justifyContent: 'center',
          columnGap: 56,
          rowGap: 10,
          marginTop: 'auto',
        }}
      >
        {LEGEND_ITEMS.map((item) => (
          <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <span style={{ width: 8, height: 8, borderRadius: 3, background: item.color, display: 'inline-block', flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: '#8c8c8c', whiteSpace: 'nowrap' }}>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}, arePropsEqual);

export default PieChartCard;
